import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeftIcon,
  CalendarIcon, 
  ClockIcon, 
  MapPinIcon, 
  CurrencyDollarIcon,
  CheckCircleIcon,
  XCircleIcon,
  GlobeAltIcon,
  BuildingOfficeIcon,
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  PlayIcon,
  StopIcon,
  XMarkIcon,
  UsersIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import jobAPI from '../services/jobAPI';
import { interpreterAPI } from '../services/api';
import Button from '../components/ui/Button';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import InterpreterJobWorkflow from '../components/InterpreterJobWorkflow';
import { useAuth } from '../contexts/AuthContext';
import { useJobRestrictions } from '../contexts/JobRestrictionContext';
import { formatDate, formatDateShort, formatTime, formatCurrency, getTimeUntilJob } from '../utils/dateUtils';
import { milesInputToNumber, isPartialMilesInput } from '../utils/mileageInputUtils';
import { getProviderJobStatusLabel, isProviderJobCompleted } from '../utils/providerJobStatus';
import FacilityDurationFollowUpNotice from '../components/FacilityDurationFollowUpNotice';
import ProviderInvoiceNotice from '../components/ProviderInvoiceNotice';
import { isJobAssignedToCurrentInterpreter } from '../utils/claimantPrivacy';

function completionReportFollowUps(data) {
  if (!data || typeof data !== 'object') return [];
  if (Array.isArray(data.follow_ups) && data.follow_ups.length) return data.follow_ups;
  if (data.follow_up) return [data.follow_up];
  if (data.follow_up_date) {
    return [{
      date: data.follow_up_date,
      time: data.follow_up_time,
      formatted_address: data.follow_up_formatted_address
    }];
  }
  return [];
}

function followUpLocationText(followUp) {
  if (!followUp) return '';
  const sameLocation = followUp.use_same_location === true || followUp.use_same_location === 'Yes';
  if (sameLocation) return 'Same as this appointment';

  const location = followUp.location && typeof followUp.location === 'object' ? followUp.location : {};
  const formatted = followUp.formatted_address || location.formatted_address;
  if (formatted) return formatted;

  const street = location.street || followUp.street;
  const city = location.city || followUp.city;
  const state = location.state || followUp.state;
  const zip = location.zip || followUp.zip;
  const cityLine = [city, state].filter(Boolean).join(', ');
  return [street, cityLine, zip].filter(Boolean).join(' ');
}

const LAST_LIST_ROUTE_KEY = 'interpreterLastJobListRoute';
const DEFAULT_RETURN_PATH = '/jobs';
const TWO_HOUR_MINIMUM_MINUTES = 120;
const CERT_NUMBER_REQUIRED_SERVICE_TYPE_CODES = ['legal', 'video', 'medical'];
const NEW_TEAM_MEMBER_OPTION = 'new';

const getDatePart = (dateValue) => {
  if (!dateValue) return null;
  const match = String(dateValue).match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
};

const getTimePart = (timeValue) => {
  if (!timeValue) return null;
  const match = String(timeValue).match(/^(\d{2}:\d{2}(?::\d{2})?)/);
  return match ? match[1] : null;
};

const buildJobDateTime = (dateValue, timeValue) => {
  const datePart = getDatePart(dateValue);
  const timePart = getTimePart(timeValue);
  if (!datePart || !timePart) return null;

  const dateTime = new Date(`${datePart}T${timePart}`);
  return Number.isNaN(dateTime.getTime()) ? null : dateTime;
};

const JobDetails = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { canAcceptJobs, showJobAcceptanceBlocked } = useJobRestrictions();
  const { profile } = useAuth();
  const [job, setJob] = useState(null);
  const [isUnavailableToInterpreter, setIsUnavailableToInterpreter] = useState(false);
  const [unavailableMessage, setUnavailableMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmationLoading, setConfirmationLoading] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationNotes, setConfirmationNotes] = useState('');
  const [showMileagePrompt, setShowMileagePrompt] = useState(false);
  const [showMileageFields, setShowMileageFields] = useState(false);
  const [mileageMilesInput, setMileageMilesInput] = useState('0');
  const mileageRequested = milesInputToNumber(mileageMilesInput);
  const [mileageRate, setMileageRate] = useState(0.70);
  const [mileagePromptLoading, setMileagePromptLoading] = useState(false);
  const FEDERAL_MILEAGE_CAP = 0.72;
  const [teamMembers, setTeamMembers] = useState([]);
  const [showTeamMemberModal, setShowTeamMemberModal] = useState(false);
  const [selectedTeamMemberId, setSelectedTeamMemberId] = useState('');
  const [assigningTeamMember, setAssigningTeamMember] = useState(false);
  const [teamMemberCertNumber, setTeamMemberCertNumber] = useState('');
  const [confirmTeamMemberId, setConfirmTeamMemberId] = useState('');
  const [confirmCertNumber, setConfirmCertNumber] = useState('');
  const [editingConfirmCert, setEditingConfirmCert] = useState(false);
  const [editingTeamMemberCert, setEditingTeamMemberCert] = useState(false);
  const [newMemberFirstName, setNewMemberFirstName] = useState('');
  const [newMemberLastName, setNewMemberLastName] = useState('');

  const getStoredReturnPath = useCallback(() => {
    const statePath = location.state?.returnTo;
    if (statePath) {
      return statePath;
    }

    if (typeof window !== 'undefined') {
      const savedRoute = localStorage.getItem(LAST_LIST_ROUTE_KEY);
      if (savedRoute) {
        return savedRoute;
      }
    }

    return DEFAULT_RETURN_PATH;
  }, [location.state]);

  const navigateBackToPreviousPage = useCallback(() => {
    navigate(getStoredReturnPath());
  }, [getStoredReturnPath, navigate]);

  useEffect(() => {
    loadJobDetails();
  }, [jobId]);

  useEffect(() => {
    if (!profile?.is_agency) return undefined;

    const loadTeamMembers = async () => {
      try {
        const response = await interpreterAPI.getAgencyMembers();
        if (response.data.success) {
          setTeamMembers(response.data.data.members || []);
        }
      } catch (error) {
        console.error('Error loading team members:', error);
      }
    };

    loadTeamMembers();
  }, [profile?.is_agency]);

  // Email "CONFIRM ASSIGNMENT" links include ?confirmAvailability=1 — open modal once job + profile are ready
  useEffect(() => {
    if (!job || loading) return;
    const params = new URLSearchParams(location.search);
    if (params.get('confirmAvailability') !== '1') return;
    if (profile === null) return;

    const isAssignedToCurrentUser = !!(
      profile?.id && String(profile.id) === String(job.assigned_interpreter_id)
    );
    const shouldPrompt =
      job.status === 'assigned' && job.confirmation_status === 'pending';

    if (isAssignedToCurrentUser && shouldPrompt) {
      setShowConfirmationModal(true);
    }

    params.delete('confirmAvailability');
    const qs = params.toString();
    navigate(`${location.pathname}${qs ? `?${qs}` : ''}`, { replace: true });
  }, [job, loading, location.search, location.pathname, profile, navigate]);

  useEffect(() => {
    if (!showMileagePrompt) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [showMileagePrompt]);

  const loadJobDetails = async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      setIsUnavailableToInterpreter(false);
      setUnavailableMessage('');
      const response = await jobAPI.getJobById(jobId);
      setJob(response.data.data);
    } catch (error) {
      console.error('Error loading job details:', error);
      const code = error.response?.data?.code;
      const isJobUnavailable =
        error.response?.status === 403 &&
        (code === 'JOB_ASSIGNED_TO_ANOTHER_INTERPRETER' || code === 'JOB_NOT_AVAILABLE');

      if (isJobUnavailable) {
        setIsUnavailableToInterpreter(true);
        setUnavailableMessage(
          code === 'JOB_ASSIGNED_TO_ANOTHER_INTERPRETER'
            ? 'This appointment was assigned to another interpreter and is no longer available to you.'
            : 'This appointment is no longer available.'
        );
        setJob(null);
      } else {
        toast.error('Failed to load job details');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const isAfterArrivalTime = useCallback(() => {
    if (!job?.scheduled_date) return true;

    const arrivalOrScheduledTime = job.arrival_time || job.scheduled_time;
    if (!arrivalOrScheduledTime) return true;

    const arrivalDateTime = buildJobDateTime(job.scheduled_date, arrivalOrScheduledTime);
    if (!arrivalDateTime) return true;

    return new Date() >= arrivalDateTime;
  }, [job]);

  const assignedToCurrentInterpreter = isJobAssignedToCurrentInterpreter(job, profile?.id);
  const canSetTeamMember =
    profile?.is_agency &&
    assignedToCurrentInterpreter &&
    ['assigned', 'reminders_sent', 'in_progress'].includes(job?.status);
  const requiresCertNumber = CERT_NUMBER_REQUIRED_SERVICE_TYPE_CODES.includes(job?.service_type_code);

  const getSavedCertNumberFor = (memberId) => {
    if (!memberId || memberId === NEW_TEAM_MEMBER_OPTION) return '';
    if (String(memberId) === String(job?.team_member_id) && job?.team_member_certification_number) {
      return job.team_member_certification_number;
    }
    const member = teamMembers.find((m) => String(m.id) === String(memberId));
    return member?.saved_certification_number || '';
  };

  const confirmationReason = job?.confirmation_reason || 'initial_assignment';
  const promptForTeamMemberOnConfirm =
    profile?.is_agency &&
    ['initial_assignment', '2day_reminder'].includes(confirmationReason);

  useEffect(() => {
    if (!showConfirmationModal || !promptForTeamMemberOnConfirm) return;
    const initialId = job?.team_member_id
      ? String(job.team_member_id)
      : teamMembers.length === 0 ? NEW_TEAM_MEMBER_OPTION : '';
    setConfirmTeamMemberId(initialId);
    setConfirmCertNumber(getSavedCertNumberFor(initialId));
    setEditingConfirmCert(false);
    setNewMemberFirstName('');
    setNewMemberLastName('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showConfirmationModal, promptForTeamMemberOnConfirm]);

  useEffect(() => {
    if (
      showConfirmationModal &&
      teamMembers.length > 0 &&
      confirmTeamMemberId === NEW_TEAM_MEMBER_OPTION &&
      !newMemberFirstName &&
      !newMemberLastName
    ) {
      setConfirmTeamMemberId('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamMembers]);

  const handleConfirmTeamMemberChange = (value) => {
    setConfirmTeamMemberId(value);
    setConfirmCertNumber(getSavedCertNumberFor(value));
    setEditingConfirmCert(false);
  };

  const confirmSavedCertNumber = getSavedCertNumberFor(confirmTeamMemberId);
  const teamMemberSavedCertNumber = getSavedCertNumberFor(selectedTeamMemberId);

  const isAddingNewTeamMember = confirmTeamMemberId === NEW_TEAM_MEMBER_OPTION;
  const agencyConfirmReady =
    !promptForTeamMemberOnConfirm ||
    (!!confirmTeamMemberId &&
      (!isAddingNewTeamMember || (newMemberFirstName.trim() && newMemberLastName.trim())) &&
      (!requiresCertNumber || confirmCertNumber.trim()));

  const showJobTimingModule =
    assignedToCurrentInterpreter &&
    (job.status === 'assigned' || job.status === 'reminders_sent' || job.status === 'in_progress');

  const [jobTimingPopupDismissed, setJobTimingPopupDismissed] = useState(false);

  useEffect(() => {
    setJobTimingPopupDismissed(false);
  }, [jobId]);

  /** After arrival: Job Timing pops up once; dismiss returns the card to the sidebar */
  const showJobTimingPopup =
    showJobTimingModule &&
    isAfterArrivalTime() &&
    !jobTimingPopupDismissed;

  useEffect(() => {
    if (!showJobTimingPopup) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [showJobTimingPopup]);

  const getCompletionElapsedMinutes = useCallback(() => {
    if (!job) return null;

    let referenceTime = null;

    if (job.job_started_at || job.in_progress_at) {
      referenceTime = new Date(job.job_started_at || job.in_progress_at);
    } else if (job.scheduled_date && (job.arrival_time || job.scheduled_time)) {
      referenceTime = buildJobDateTime(job.scheduled_date, job.arrival_time || job.scheduled_time);
    }

    if (!referenceTime || Number.isNaN(referenceTime.getTime())) {
      return null;
    }

    return (Date.now() - referenceTime.getTime()) / (1000 * 60);
  }, [job]);

  const handleJobAction = async (action, data = {}) => {
    try {
      setActionLoading(true);
      let response;
      switch (action) {
        case 'accept':
          // Check if user can accept jobs (no overdue reports)
          if (!canAcceptJobs()) {
            showJobAcceptanceBlocked();
            setActionLoading(false);
            return;
          }
          // Show mileage prompt before accepting
          setShowMileagePrompt(true);
          setShowMileageFields(false);
          setActionLoading(false);
          return; // Don't proceed with acceptance yet
        case 'decline':
          response = await jobAPI.declineJob(jobId, data);
          toast.success('Job declined');
          break;
        case 'start':
          // Get location data for non-remote jobs
          let locationData = {};
          if (!job.is_remote) {
            try {
              const position = await new Promise((resolve, reject) => {
                if (!navigator.geolocation) {
                  reject(new Error('Geolocation is not supported by your browser'));
                  return;
                }
                navigator.geolocation.getCurrentPosition(resolve, reject, {
                  enableHighAccuracy: true,
                  timeout: 10000,
                  maximumAge: 60000
                });
              });
              locationData = {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude
              };
            } catch (geoError) {
              console.warn('Geolocation error:', geoError);
              toast.error('Unable to get your location. Please enable location services and try again.');
              setActionLoading(false);
              return;
            }
          }
          response = await jobAPI.startJob(jobId, locationData);
          toast.success('Job started successfully!');
          // Reload job details to show updated status
          await loadJobDetails();
          return; // Don't navigate away
        case 'end':
          {
            const elapsedMinutes = getCompletionElapsedMinutes();
            if (elapsedMinutes !== null && elapsedMinutes < TWO_HOUR_MINIMUM_MINUTES) {
              const roundedElapsed = Math.max(0, Math.floor(elapsedMinutes));
              const shouldContinue = window.confirm(
                `This job is being completed before the 2-hour minimum (${roundedElapsed} minutes elapsed). Do you want to continue?`
              );

              if (!shouldContinue) {
                return;
              }
            }
          }
          response = await jobAPI.endJob(jobId);
          toast.success('Job completed successfully!');
          // Reload job details to show updated status
          await loadJobDetails();
          return; // Don't navigate away
        default:
          return;
      }
      
      // Navigate back to the previous page (only for accept/decline)
      navigateBackToPreviousPage();
    } catch (error) {
      console.error(`Error ${action}ing job:`, error);
      const errorMessage = error.response?.data?.message || `Failed to ${action} job`;
      toast.error(errorMessage);
    } finally {
      setActionLoading(false);
    }
  };

  const createTeamMemberForConfirmation = async () => {
    try {
      const response = await interpreterAPI.createTeamMember({
        first_name: newMemberFirstName.trim(),
        last_name: newMemberLastName.trim(),
        languages: [job.language_id].filter(Boolean),
        certifications: [job.service_type_id].filter(Boolean)
      });
      const created = response.data?.data;
      if (!response.data?.success || !created?.id) {
        toast.error(response.data?.message || 'Failed to add team member');
        return null;
      }
      setTeamMembers((prev) => [{ ...created, saved_certification_number: null }, ...prev]);
      setConfirmTeamMemberId(String(created.id));
      return created.id;
    } catch (error) {
      console.error('Error adding team member:', error);
      toast.error(error.response?.data?.message || 'Failed to add team member');
      return null;
    }
  };

  const handleConfirmation = async (confirmationStatus) => {
    if (
      confirmationStatus === 'declined' &&
      !window.confirm(
        "If you can't attend, you will be unassigned from this job and we will find another interpreter. Do you want to continue?"
      )
    ) {
      return;
    }

    try {
      setConfirmationLoading(true);

      const payload = {
        confirmation_status: confirmationStatus,
        confirmation_notes: confirmationNotes
      };

      if (confirmationStatus === 'confirmed' && promptForTeamMemberOnConfirm) {
        let teamMemberId = confirmTeamMemberId;
        if (teamMemberId === NEW_TEAM_MEMBER_OPTION) {
          teamMemberId = await createTeamMemberForConfirmation();
          if (!teamMemberId) return;
        }
        payload.team_member_id = parseInt(teamMemberId, 10);
        payload.certification_number = requiresCertNumber ? confirmCertNumber.trim() : null;
      }
      
      const response = await jobAPI.confirmAvailability(jobId, payload);
      
      if (response.data.success) {
        setShowConfirmationModal(false);
        setConfirmationNotes('');

        if (confirmationStatus === 'declined') {
          toast.success('You have been unassigned from this job.');
          navigateBackToPreviousPage();
          return;
        }

        toast.success('Availability confirmed successfully!');
        await loadJobDetails();
      }
    } catch (error) {
      console.error('Error confirming availability:', error);
      const errorMessage = error.response?.data?.message || 'Failed to confirm availability';
      toast.error(errorMessage);
    } finally {
      setConfirmationLoading(false);
    }
  };

  // New availability indication handlers
  const handleIndicateAvailability = () => {
    // For in-person jobs, show mileage modal first
    if (!job.is_remote) {
      setShowMileagePrompt(true);
      setShowMileageFields(false);
    } else {
      // For remote jobs, indicate availability directly
      confirmAvailability(0);
    }
  };
  
  const confirmAvailability = async (mileage, rate = null) => {
    try {
      setMileagePromptLoading(true);
      const miles = parseFloat(mileage) || 0;
      const effectiveRate = rate != null ? Math.min(FEDERAL_MILEAGE_CAP, Math.max(0, parseFloat(rate) || 0.70)) : null;
      const response = await jobAPI.indicateAvailability(jobId, miles, effectiveRate);
      
      // Check if auto-assigned as preferred provider
      if (response.data?.auto_assigned) {
        toast.success(
          '🎉 Congratulations! As a preferred provider, you have been automatically assigned to this job!',
          { duration: 6000 }
        );
      } else {
        const message = miles > 0 
          ? `Availability indicated with ${miles} miles mileage request! The admin will review and assign.`
          : 'Availability indicated! The admin will review responses and assign an interpreter.';
        toast.success(message);
      }
      
      setShowMileagePrompt(false);
      setShowMileageFields(false);
      setMileageMilesInput('0');
      setMileageRate(0.70);
      await loadJobDetails();
    } catch (error) {
      console.error('Error indicating availability:', error);
      toast.error(error.response?.data?.message || 'Failed to indicate availability');
    } finally {
      setMileagePromptLoading(false);
    }
  };

  const handleIndicateNotAvailable = async () => {
    const reason = window.prompt('Please provide a reason (optional):');
    if (reason === null) return; // User cancelled
    
    try {
      setActionLoading(true);
      await jobAPI.indicateNotAvailable(jobId, reason || '');
      toast.success('Response recorded. Thank you for letting us know.');
      await loadJobDetails();
    } catch (error) {
      console.error('Error indicating not available:', error);
      toast.error(error.response?.data?.message || 'Failed to record response');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMileageSubmit = async () => {
    // Double-check restriction before submitting
    if (!canAcceptJobs()) {
      showJobAcceptanceBlocked();
      return;
    }
    
    setMileagePromptLoading(true);
    try {
      // Check if this is for availability indication or job acceptance
      if (job.status === 'finding_interpreter') {
        await confirmAvailability(mileageRequested, mileageRate);
      } else {
        const effectiveRate = mileageRequested > 0 ? Math.min(FEDERAL_MILEAGE_CAP, Math.max(0, parseFloat(mileageRate) || 0.70)) : null;
        const response = await jobAPI.acceptJob(jobId, { 
          mileage_requested: mileageRequested,
          mileage_rate: effectiveRate
        });
        
        toast.success('Job accepted successfully! Your mileage request is pending admin approval.');
        setShowMileagePrompt(false);
        setShowMileageFields(false);
        setMileageMilesInput('0');
        setMileageRate(0.70);
        navigateBackToPreviousPage();
      }
    } catch (error) {
      console.error('Error submitting mileage request:', error);
      toast.error(`Failed to submit mileage request: ${error.response?.data?.message || error.message}`);
    } finally {
      setMileagePromptLoading(false);
    }
  };

  const handleNoMileage = async () => {
    // Double-check restriction before submitting
    if (!canAcceptJobs()) {
      showJobAcceptanceBlocked();
      return;
    }
    
    setMileagePromptLoading(true);
    try {
      // Check if this is for availability indication or job acceptance
      if (job.status === 'finding_interpreter') {
        await confirmAvailability(0);
      } else {
        const response = await jobAPI.acceptJob(jobId, {});
        
        toast.success('Job accepted successfully!');
        setShowMileagePrompt(false);
        setShowMileageFields(false);
        setMileageMilesInput('0');
        setMileageRate(0.70);
        navigateBackToPreviousPage();
      }
    } catch (error) {
      console.error('Error accepting job:', error);
      toast.error(`Failed to accept job: ${error.response?.data?.message || error.message}`);
    } finally {
      setMileagePromptLoading(false);
    }
  };

  const openTeamMemberModal = () => {
    const currentId = job?.team_member_id ? String(job.team_member_id) : '';
    const matchedByName = !currentId && job?.team_member_first_name
      ? teamMembers.find(
          (member) =>
            member.first_name === job.team_member_first_name &&
            member.last_name === job.team_member_last_name
        )
      : null;
    const initialId = currentId || (matchedByName ? String(matchedByName.id) : '');
    setSelectedTeamMemberId(initialId);
    setTeamMemberCertNumber(getSavedCertNumberFor(initialId));
    setEditingTeamMemberCert(false);
    setShowTeamMemberModal(true);
  };

  const handleSelectTeamMember = (memberId) => {
    setSelectedTeamMemberId(memberId);
    setTeamMemberCertNumber(getSavedCertNumberFor(memberId));
    setEditingTeamMemberCert(false);
  };

  const handleAssignTeamMember = async () => {
    if (!selectedTeamMemberId) {
      toast.error('Please select a team member');
      return;
    }
    if (requiresCertNumber && !teamMemberCertNumber.trim()) {
      toast.error("Please enter the team member's certification number");
      return;
    }

    try {
      setAssigningTeamMember(true);
      await jobAPI.assignTeamMember(
        jobId,
        parseInt(selectedTeamMemberId, 10),
        requiresCertNumber ? teamMemberCertNumber.trim() : null
      );
      toast.success('Team member assigned');
      setShowTeamMemberModal(false);
      await loadJobDetails({ silent: true });
    } catch (error) {
      console.error('Error assigning team member:', error);
      toast.error(error.response?.data?.message || 'Failed to assign team member');
    } finally {
      setAssigningTeamMember(false);
    }
  };

  // Date/time formatting functions imported from utils/dateUtils.js

  const calculateEarnings = (job) => {
    // Priority 1: Use interpreter_paid_amount if job is marked as paid
    if (job?.interpreter_paid_amount && (job?.status === 'interpreter_paid' || job?.interpreter_paid_at)) {
      return parseFloat(job.interpreter_paid_amount);
    }

    // Priority 2: Use estimated_earnings from backend (includes billing increments, minimum hours, and mileage)
    if (job?.estimated_earnings) {
      return parseFloat(job.estimated_earnings);
    }

    // Priority 3: Use calculated_total_payment from backend
    if (job?.calculated_total_payment) {
      return parseFloat(job.calculated_total_payment);
    }

    // Fallback: Calculate locally
    let basePayment = 0;
    
    // If interpreter has custom service rates, calculate based on those
    if (profile?.service_rates && job.estimated_duration_minutes) {
      const serviceRate = profile.service_rates.find(
        rate => rate.service_type_id === job.service_type_id
      );
      
      if (serviceRate && serviceRate.rate_amount) {
        const hours = job.estimated_duration_minutes / 60;
        const rateUnit = (serviceRate.rate_unit || 'hours').toLowerCase();
        
        if (rateUnit === 'minutes') {
          basePayment = serviceRate.rate_amount * job.estimated_duration_minutes;
        } else {
          // Convert block rates (e.g. '3hours', '6hours') to effective hourly rate
          const blockMatch = rateUnit.match(/^(\d+)hours?$/);
          const effectiveHourlyRate = blockMatch
            ? serviceRate.rate_amount / parseInt(blockMatch[1], 10)
            : serviceRate.rate_amount;
          basePayment = effectiveHourlyRate * hours;
        }
      }
    }

    // Otherwise, use the job's total_amount (calculated by backend) if it's greater than 0
    if (basePayment === 0) {
      const totalAmount = parseFloat(job.total_amount) || 0;
      if (totalAmount > 0) {
        basePayment = totalAmount;
      }
    }

    // Fallback: calculate from hourly rate and duration
    if (basePayment === 0 && job.hourly_rate && job.estimated_duration_minutes) {
      const hours = job.estimated_duration_minutes / 60;
      basePayment = parseFloat(job.hourly_rate) * hours;
    }
    
    // Add mileage reimbursement if available
    const mileageReimbursement = parseFloat(job.mileage_reimbursement) || 0;
    
    return basePayment + mileageReimbursement;
  };

  const getBillableHours = (job) => {
    const estimatedMinutes = parseFloat(job.estimated_duration_minutes) || 0;
    const minimumHours = parseFloat(job.interpreter_minimum_hours) || 1;
    const minimumMinutes = minimumHours * 60;
    const billingIncrement = parseFloat(job.interpreter_interval_minutes) || 15;
    const finalBillableMinutes = Math.max(estimatedMinutes, minimumMinutes);
    const roundedMinutes =
      finalBillableMinutes <= minimumMinutes
        ? finalBillableMinutes
        : minimumMinutes +
          Math.ceil((finalBillableMinutes - minimumMinutes) / billingIncrement) * billingIncrement;

    return {
      billableHours: roundedMinutes / 60,
      roundedMinutes,
    };
  };

  const formatEarningsBreakdown = (job) => {
    if (job.use_interpreter_flat_rate && parseFloat(job.interpreter_flat_rate || 0) > 0) {
      const flatRateAmount = parseFloat(job.interpreter_flat_rate);
      const flatRateHours = parseFloat(job.interpreter_flat_rate_hours || 3);
      const estimatedHours = (parseFloat(job.estimated_duration_minutes) || 0) / 60;
      if (flatRateHours > 0 && estimatedHours > flatRateHours) {
        const blocks = Math.ceil(estimatedHours / flatRateHours);
        return `${formatCurrency(flatRateAmount)} flat rate × ${blocks} blocks = ${formatCurrency(flatRateAmount * blocks)}`;
      }
      return `${formatCurrency(flatRateAmount)} flat rate (covers ${flatRateHours} hours)`;
    }

    const { billableHours, roundedMinutes } = getBillableHours(job);

    // Backend-resolved rate (service area, language-specific, or profile)
    if (job.interpreter_original_rate_amount != null && job.interpreter_rate_unit) {
      const amount = parseFloat(job.interpreter_original_rate_amount);
      const rateUnit = (job.interpreter_rate_unit || 'hours').toLowerCase();

      if (rateUnit === 'minutes') {
        return `${formatCurrency(amount)}/min × ${roundedMinutes} min = ${formatCurrency(amount * roundedMinutes)}`;
      }
      if (rateUnit === 'word') {
        const wordCount = parseInt(job.estimated_word_count || 0, 10);
        return `${formatCurrency(amount)}/word × ${wordCount} words = ${formatCurrency(amount * wordCount)}`;
      }

      const blockMatch = rateUnit.match(/^(\d+)hours?$/);
      if (blockMatch) {
        const blockHours = parseInt(blockMatch[1], 10);
        const effectiveHourly = amount / blockHours;
        return `${formatCurrency(amount)}/${blockHours}hr (${formatCurrency(effectiveHourly)}/hr) × ${billableHours.toFixed(1)} hours = ${formatCurrency(effectiveHourly * billableHours)}`;
      }

      return `${formatCurrency(amount)}/hour × ${billableHours.toFixed(1)} hours = ${formatCurrency(amount * billableHours)}`;
    }

    if (job.hourly_rate) {
      const hourly = parseFloat(job.hourly_rate);
      return `${formatCurrency(hourly)}/hour × ${billableHours.toFixed(1)} hours = ${formatCurrency(hourly * billableHours)}`;
    }

    if (profile?.service_rates) {
      const serviceRate = profile.service_rates.find(
        (rate) => rate.service_type_id === job.service_type_id
      );
      if (serviceRate?.rate_amount && serviceRate.rate_unit) {
        const rateUnit = (serviceRate.rate_unit || 'hours').toLowerCase();
        if (rateUnit === 'minutes') {
          return `${formatCurrency(serviceRate.rate_amount)}/min × ${roundedMinutes} min = ${formatCurrency(serviceRate.rate_amount * roundedMinutes)}`;
        }
        const blockMatch = rateUnit.match(/^(\d+)hours?$/);
        if (blockMatch) {
          const blockHours = parseInt(blockMatch[1], 10);
          const effectiveHourly = serviceRate.rate_amount / blockHours;
          return `${formatCurrency(serviceRate.rate_amount)}/${blockHours}hr (${formatCurrency(effectiveHourly)}/hr) × ${billableHours.toFixed(1)} hours = ${formatCurrency(effectiveHourly * billableHours)}`;
        }
        return `${formatCurrency(serviceRate.rate_amount)}/hour × ${billableHours.toFixed(1)} hours = ${formatCurrency(serviceRate.rate_amount * billableHours)}`;
      }
    }

    return 'Rate not set';
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'text-red-600 bg-red-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'normal': return 'text-blue-600 bg-blue-100';
      case 'low': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (!job) {
    if (isUnavailableToInterpreter) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center max-w-lg px-6">
            <h2 className="text-xl font-semibold text-gray-900">
              {unavailableMessage || 'This appointment is no longer available.'}
            </h2>
            <Button
              className="mt-6"
              onClick={navigateBackToPreviousPage}
            >
              Back to Previous Page
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900">Job not found</h2>
          <p className="text-gray-600 mt-2">The job you're looking for doesn't exist or has been removed.</p>
          <Button
            className="mt-4"
            onClick={navigateBackToPreviousPage}
          >
            Back to Previous Page
          </Button>
        </div>
      </div>
    );
  }

  const dismissJobTimingPopup = () => setJobTimingPopupDismissed(true);

  const jobTimingInner = (
    <div className="space-y-4">
      <div className="text-center">
        <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
          job.status === 'in_progress'
            ? 'bg-blue-100 text-blue-800'
            : 'bg-green-100 text-green-800'
        }`}>
          <ClockIcon className="h-4 w-4 mr-2" />
          Status: {job.status === 'in_progress' ? 'In Progress' : 'Ready to Start'}
        </div>
      </div>

      <div className="space-y-3">
        {(job.status === 'assigned' || job.status === 'reminders_sent') && (
          <Button
            className="w-full"
            onClick={() => handleJobAction('start')}
            disabled={actionLoading}
          >
            <PlayIcon className="h-4 w-4 mr-2" />
            {actionLoading ? 'Starting...' : 'Start Job'}
          </Button>
        )}

        {job.status === 'in_progress' && (
          <Button
            className="w-full"
            onClick={() => handleJobAction('end')}
            disabled={actionLoading}
          >
            <StopIcon className="h-4 w-4 mr-2" />
            {actionLoading ? 'Completing...' : 'Complete Job'}
          </Button>
        )}

        {(job.status === 'assigned' || job.status === 'reminders_sent') && (
          <Button
            className="w-full"
            onClick={() => handleJobAction('end')}
            disabled={actionLoading || !isAfterArrivalTime()}
            variant="secondary"
          >
            <StopIcon className="h-4 w-4 mr-2" />
            {actionLoading ? 'Completing...' : 'Complete Job'}
          </Button>
        )}
      </div>

      <div className="text-sm text-gray-600 text-center">
        {job.status === 'in_progress' ? (
          <p>Click "Complete Job" when the appointment is complete</p>
        ) : (
          <p>
            You can start the job at the appointment location, or complete it directly after the arrival time.
          </p>
        )}
        {(job.status === 'assigned' || job.status === 'reminders_sent') && !isAfterArrivalTime() && (
          <p className="mt-2 text-amber-600">
            "Complete Job" becomes available after the scheduled arrival time.
          </p>
        )}
      </div>
    </div>
  );

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <button
                onClick={navigateBackToPreviousPage}
                className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back to Previous Page
              </button>
              {/* Don't show job number for available jobs or when assigned to another interpreter */}
              <h1 className="text-3xl font-semibold tracking-tight text-ink">
                {job.status === 'finding_interpreter' || (job.status === 'assigned' && job.assigned_interpreter_id && profile?.id && String(profile.id) !== String(job.assigned_interpreter_id))
                  ? (job.service_type_name || job.title || 'Job Opportunity')
                  : (job.job_number || job.title)}
              </h1>
              <div className="flex items-center mt-2 space-x-2">
                {job.is_rush_request && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800 border border-red-200">
                    RUSH Request
                  </span>
                )}
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(job.priority)}`}>
                  {job.priority} Priority
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                  <BuildingOfficeIcon className="h-4 w-4 mr-1" />
                  {job.job_type}
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Special Requirements / Notes */}
            {(job.special_requirements || job.notes) && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
              >
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Special Requirements</h2>
                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{job.special_requirements || job.notes}</p>
              </motion.div>
            )}

            {/* Job Details */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
            >
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Job Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center">
                    <CalendarIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Date</p>
                      <p className="mt-1 text-sm font-medium text-gray-900">{formatDate(job.scheduled_date)}</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <ClockIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Appointment Time</p>
                      <p className="mt-1 text-sm font-medium text-gray-900">{formatTime(job.scheduled_time)}</p>
                    </div>
                  </div>
                  {job.arrival_time && (
                    <div className="flex items-center">
                      <ClockIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Arrival Time</p>
                        <p className="mt-1 text-sm font-medium text-blue-700">{formatTime(job.arrival_time)}</p>
                      </div>
                    </div>
                  )}
                  {job.estimated_duration_minutes && (
                    <div className="flex items-center">
                      <ClockIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Reserve Hours</p>
                        <p className="mt-1 text-sm font-medium text-gray-900">{(job.estimated_duration_minutes / 60).toFixed(1)} hours</p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-start">
                    {job.is_remote ? (
                      <GlobeAltIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                    ) : (
                      <MapPinIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                    )}
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Location</p>
                      {!job.is_remote && job.service_location_name && (
                        <p className="mt-1 text-sm font-medium text-gray-900">{job.service_location_name}</p>
                      )}
                      <p className={`${job.service_location_name ? '' : 'mt-1 '}text-sm font-medium text-gray-900`}>
                        {job.is_remote ? 'Remote Session' : (job.location_address || job.service_location_address || `${job.location_city || job.service_location_city || ''}, ${job.location_state || job.service_location_state || ''}`.replace(/^, $/, 'N/A'))}
                      </p>
                      {/* Location Contact - Show if name exists */}
                      {job.location_contact_name && job.location_contact_name.trim() && (
                        <div className="mt-2 pt-2 border-t border-gray-200">
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Location Contact</p>
                          <p className="text-sm text-gray-900">
                            {job.location_contact_name}
                            {job.location_contact_title && job.location_contact_title.trim() && ` - ${job.location_contact_title}`}
                          </p>
                          {job.location_contact_phone && job.location_contact_phone.trim() && 
                           (!job.facility_phone || job.location_contact_phone.trim() !== job.facility_phone.trim()) && (
                            <p className="text-sm text-gray-600">{job.location_contact_phone}</p>
                          )}
                        </div>
                      )}
                      {/* Facility Phone - Only show if no location contact name */}
                      {(!job.location_contact_name || !job.location_contact_name.trim()) && 
                       job.facility_phone && (
                        <div className="mt-2 pt-2 border-t border-gray-200">
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Facility Phone</p>
                          <p className="text-sm text-gray-600">{job.facility_phone}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Languages</p>
                    <p className="mt-1 text-sm font-medium text-gray-900">{job.source_language_name || job.language_name || 'N/A'} → {job.target_language_name || 'English'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Service Type</p>
                    <p className="mt-1 text-sm font-medium text-gray-900">{job.service_type_name}</p>
                  </div>
                  {job.appointment_type && (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Appointment Type</p>
                      <p className="mt-1 text-sm font-medium text-gray-900">
                        {job.appointment_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Status</p>
                    <p className="mt-1 text-sm font-medium text-gray-900">{getProviderJobStatusLabel(job)}</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Claimant Information — only visible once assigned to this interpreter */}
            {assignedToCurrentInterpreter && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
            >
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Claimant Information</h2>
              <div className="space-y-4">
                <div className="flex items-center">
                  <UserIcon className="h-5 w-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Claimant Name</p>
                    <p className="text-sm text-gray-600">
                      {job.claimant_name || job.client_name || 'N/A'}
                    </p>
                  </div>
                </div>
                {job.claimant_phone && (
                  <div className="flex items-center">
                    <PhoneIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Phone</p>
                      <p className="text-sm text-gray-600">{job.claimant_phone}</p>
                    </div>
                  </div>
                )}
                {job.claimant_address && (
                  <div className="flex items-center">
                    <MapPinIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Address</p>
                      <p className="text-sm text-gray-600">
                        {job.claimant_address}
                        {job.claimant_city && job.claimant_state && (
                          <span>, {job.claimant_city}, {job.claimant_state}</span>
                        )}
                        {job.claimant_zip_code && (
                          <span> {job.claimant_zip_code}</span>
                        )}
                      </p>
                    </div>
                  </div>
                )}
                {job.client_email && (
                  <div className="flex items-center">
                    <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Client Email</p>
                      <p className="text-sm text-gray-600">{job.client_email}</p>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
            )}

            {/* Completion Report */}
            {job.completion_report_submitted && job.completion_report_data && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
              >
                <div className="flex items-center mb-4 bg-green-50 -m-6 p-4 rounded-t-lg border-b border-green-200">
                  <CheckCircleIcon className="h-6 w-6 text-green-600 mr-3" />
                  <div>
                    <h2 className="text-xl font-semibold text-green-900">Completion Report</h2>
                    <p className="text-sm text-green-700 mt-1">
                      Submitted on {new Date(job.completion_report_data.submitted_at || job.completion_report_submitted_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
                
                {(job.completion_report_data.duration_exceeds_scheduled ||
                  (job.actual_duration_minutes > (job.estimated_duration_minutes || 0))) && (
                  <FacilityDurationFollowUpNotice
                    actualDurationMinutes={job.actual_duration_minutes}
                    jobData={job}
                    className="mt-6"
                  />
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  {job.completion_report_data.start_time && (
                    <div>
                      <p className="text-sm font-medium text-gray-500">Start Time</p>
                      <p className="text-sm text-gray-900 mt-1">{job.completion_report_data.start_time}</p>
                    </div>
                  )}
                  
                  {job.completion_report_data.end_time && (
                    <div>
                      <p className="text-sm font-medium text-gray-500">End Time</p>
                      <p className="text-sm text-gray-900 mt-1">{job.completion_report_data.end_time}</p>
                    </div>
                  )}
                  
                  {job.completion_report_data.start_time && job.completion_report_data.end_time && (
                    <div>
                      <p className="text-sm font-medium text-gray-500">Duration</p>
                      <p className="text-sm text-gray-900 mt-1">
                        {(() => {
                          try {
                            const start = new Date(`2000-01-01 ${job.completion_report_data.start_time}`);
                            const end = new Date(`2000-01-01 ${job.completion_report_data.end_time}`);
                            const diffMs = end - start;
                            const diffMins = Math.round(diffMs / 60000);
                            const hours = Math.floor(diffMins / 60);
                            const mins = diffMins % 60;
                            return `${hours}h ${mins}m`;
                          } catch (e) {
                            return 'N/A';
                          }
                        })()}
                      </p>
                    </div>
                  )}
                  
                  {job.completion_report_data.result && (
                    <div>
                      <p className="text-sm font-medium text-gray-500">Result</p>
                      <p className="text-sm text-gray-900 mt-1 capitalize">
                        {job.completion_report_data.result.replace(/_/g, ' ')}
                      </p>
                    </div>
                  )}
                  
                  {job.completion_report_data.file_status && (
                    <div>
                      <p className="text-sm font-medium text-gray-500">File Status</p>
                      <p className="text-sm text-gray-900 mt-1 capitalize">
                        {job.completion_report_data.file_status.replace(/_/g, ' ')}
                      </p>
                    </div>
                  )}
                </div>
                
                {job.completion_report_data.notes && (
                  <div className="mt-6">
                    <p className="text-sm font-medium text-gray-500">Notes</p>
                    <p className="text-sm text-gray-900 mt-2">{job.completion_report_data.notes}</p>
                  </div>
                )}
                
                {completionReportFollowUps(job.completion_report_data).length > 0 && (
                  <div className="mt-6 pt-6 border-t border-gray-200 space-y-6">
                    {completionReportFollowUps(job.completion_report_data).map((followUp, index, list) => {
                      const locationText = followUpLocationText(followUp);
                      return (
                        <div key={`${followUp.date || 'follow-up'}-${followUp.time || 'time'}-${index}`}>
                          <p className="text-sm font-semibold text-gray-900 mb-4">
                            {list.length > 1 ? `Follow-up ${index + 1}` : 'Follow-up Appointment'}
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {followUp.date && (
                              <div>
                                <p className="text-sm font-medium text-gray-500">Date</p>
                                <p className="text-sm text-gray-900 mt-1">{formatDateShort(followUp.date)}</p>
                              </div>
                            )}

                            {followUp.time && (
                              <div>
                                <p className="text-sm font-medium text-gray-500">Time</p>
                                <p className="text-sm text-gray-900 mt-1">{formatTime(followUp.time)}</p>
                              </div>
                            )}

                            {locationText && (
                              <div className="md:col-span-2">
                                <p className="text-sm font-medium text-gray-500">Location</p>
                                <p className="text-sm text-gray-900 mt-1">{locationText}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </motion.div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Earnings Card - Only show for the interpreter assigned to this job */}
            {(!job.assigned_interpreter_id || (profile?.id && String(profile.id) === String(job.assigned_interpreter_id))) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Earnings</h3>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600 mb-2">
                  {formatCurrency(calculateEarnings(job))}
                </div>
                <p className="text-sm text-gray-500 mb-4">
                  Total earnings for this job
                </p>
                        <div className="text-xs text-gray-400 mb-4">
                          {formatEarningsBreakdown(job)}
                        </div>
                {job.mileage_reimbursement && parseFloat(job.mileage_reimbursement) > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex justify-between items-center text-sm mb-2">
                      <span className="text-gray-600">Base Rate:</span>
                      <span className="text-gray-900 font-medium">
                        {formatCurrency(calculateEarnings(job) - parseFloat(job.mileage_reimbursement))}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">Mileage ({job.mileage_requested || 0} mi):</span>
                      <span className="text-orange-600 font-medium">
                        {formatCurrency(parseFloat(job.mileage_reimbursement))}
                      </span>
                    </div>
                  </div>
                )}
                <div className="text-sm text-gray-600 mt-4">
                  Duration: {job.estimated_duration_minutes} minutes
                </div>
              </div>
            </motion.div>
            )}

            <ProviderInvoiceNotice job={job} profile={profile} />

            {profile?.is_agency && assignedToCurrentInterpreter && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45 }}
                className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <UsersIcon className="h-5 w-5 mr-2 text-blue-600" />
                  Team Member
                </h3>
                {job.team_member_first_name ? (
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {job.team_member_first_name} {job.team_member_last_name}
                      </p>
                      {job.team_member_certification_number && (
                        <p className="text-xs text-gray-600">Cert #: {job.team_member_certification_number}</p>
                      )}
                      <p className="text-xs text-gray-500">Will perform this job</p>
                    </div>
                    {canSetTeamMember && (
                      <Button variant="outline" size="sm" onClick={openTeamMemberModal}>
                        Change
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm text-gray-500 italic">No team member assigned</p>
                    {canSetTeamMember && (
                      <Button size="sm" onClick={openTeamMemberModal}>
                        Assign
                      </Button>
                    )}
                  </div>
                )}
              </motion.div>
            )}

            {/* Action Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {job.assignment_status ? 'Status' : 'Actions'}
              </h3>
              <div className="space-y-3">
                {job.status === 'finding_interpreter' && !job.assignment_status && (
                  <>
                    <Button
                      className="w-full bg-green-600 hover:bg-green-700"
                      onClick={handleIndicateAvailability}
                      disabled={actionLoading}
                    >
                      <CheckCircleIcon className="h-4 w-4 mr-2" />
                      {actionLoading ? 'Processing...' : "I'm Available"}
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full border-red-300 text-red-700 hover:bg-red-50"
                      onClick={handleIndicateNotAvailable}
                      disabled={actionLoading}
                    >
                      <XCircleIcon className="h-4 w-4 mr-2" />
                      {actionLoading ? 'Processing...' : 'Not Available'}
                    </Button>
                  </>
                )}
                
                {job.status === 'finding_interpreter' && job.assignment_status === 'available' && (
                  <div className="p-5 bg-blue-50 border-2 border-blue-200 rounded-lg">
                    <div className="flex items-start">
                      <ClockIcon className="h-6 w-6 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-base font-semibold text-blue-900 mb-1">
                          Waiting for Admin Assignment
                        </p>
                        <p className="text-sm text-blue-700 mb-2">
                          You've indicated you're available for this job.
                        </p>
                        <div className="text-xs text-blue-600 bg-blue-100 rounded px-3 py-2">
                          💡 The admin team is reviewing available interpreters and will assign this job soon. You'll be notified once assigned.
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {job.status === 'finding_interpreter' && job.assignment_status === 'not_available' && (
                  <div className="p-5 bg-gray-50 border-2 border-gray-300 rounded-lg">
                    <div className="flex items-start">
                      <XCircleIcon className="h-6 w-6 text-gray-600 mr-3 flex-shrink-0" />
                      <div>
                        <p className="text-base font-semibold text-gray-900 mb-1">
                          Not Available
                        </p>
                        <p className="text-sm text-gray-600">
                          You've indicated you're not available for this job.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {(job.status === 'assigned' || job.status === 'reminders_sent') && job.assigned_interpreter_id ? (
                  (() => {
                    const isAssignedToCurrentUser = !!(profile?.id && String(profile.id) === String(job.assigned_interpreter_id));
                    if (!isAssignedToCurrentUser) {
                      return (
                        <div className="text-center py-4 p-4 rounded-lg bg-red-50 border border-red-200">
                          <div className="text-lg font-semibold text-red-700 mb-2">
                            Not available — assigned to another interpreter
                          </div>
                          <p className="text-sm text-red-600">
                            This appointment is no longer available to you. It has been assigned to another interpreter.
                          </p>
                        </div>
                      );
                    }
                    return (
                      <div className="text-center py-4">
                        {job.confirmation_status === 'pending' ? (
                          <div>
                            <div className="text-lg font-semibold text-orange-600 mb-2">
                              ⚠️ Availability Confirmation Required
                            </div>
                            <p className="text-sm text-gray-500 mb-4">
                              Please confirm if you can still make this appointment.
                            </p>
                            <div className="space-y-2">
                              <Button
                                className="w-full"
                                onClick={() => setShowConfirmationModal(true)}
                                disabled={confirmationLoading}
                              >
                                <CheckCircleIcon className="h-4 w-4 mr-2" />
                                Confirm Availability
                              </Button>
                            </div>
                          </div>
                        ) : job.confirmation_status === 'confirmed' ? (
                          <div>
                            <div className="text-lg font-semibold text-green-600 mb-2">
                              ✓ Availability Confirmed
                            </div>
                            <p className="text-sm text-gray-500">
                              You have confirmed you can make the appointment
                            </p>
                          </div>
                        ) : job.confirmation_status === 'declined' ? (
                          <div>
                            <div className="text-lg font-semibold text-red-600 mb-2">
                              ✗ Availability Declined
                            </div>
                            <p className="text-sm text-gray-500">
                              You have declined this appointment
                            </p>
                          </div>
                        ) : (
                          <div>
                            <div className="text-lg font-semibold text-green-600 mb-2">
                              ✓ Job Accepted
                            </div>
                            <p className="text-sm text-gray-500">
                              This job has been assigned to you
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })()
                ) : (
                  <div className="text-center py-4">
                    <div className="text-lg font-semibold text-gray-600 mb-2">
                      Job Status: {getProviderJobStatusLabel(job)}
                    </div>
                    <p className="text-sm text-gray-500">
                      {isProviderJobCompleted(job)
                        ? 'This job has been completed'
                        : 'This job is not available for acceptance'}
                    </p>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Job Timing Controls — hidden while popup is open (after arrival) */}
            {showJobTimingModule && !showJobTimingPopup ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <ClockIcon className="h-5 w-5 mr-2 text-blue-600" />
                  Job Timing
                </h3>
                {jobTimingInner}
              </motion.div>
            ) : null}
          </div>
        </div>

        {/* Job Workflow - Only show for the interpreter who is assigned to this job */}
        {job.assigned_interpreter_id && profile?.id && String(profile.id) === String(job.assigned_interpreter_id) && (
          <div className="mt-8">
            <InterpreterJobWorkflow 
              job={job} 
              onJobUpdate={(updatedJob) => {
                setJob(updatedJob);
                loadJobDetails(); // Refresh the job data
              }}
            />
          </div>
        )}
      </div>

      {/* Job Timing — modal after arrival (dismiss to use sidebar card) */}
      {showJobTimingPopup && (
        <div
          className="fixed inset-0 z-[60] flex items-end justify-center bg-black/50 p-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-[max(1rem,env(safe-area-inset-top))] sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="job-timing-popup-title"
          onClick={dismissJobTimingPopup}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ type: 'spring', damping: 26, stiffness: 340 }}
            className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              aria-label="Close"
              className="absolute right-3 top-3 rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
              onClick={dismissJobTimingPopup}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
            <h3
              id="job-timing-popup-title"
              className="text-lg font-semibold text-gray-900 mb-4 flex items-center pr-10"
            >
              <ClockIcon className="h-5 w-5 mr-2 shrink-0 text-blue-600" />
              Job Timing
            </h3>
            {jobTimingInner}
          </motion.div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmationModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900/50">
          <div className="flex min-h-full items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.18 }}
              className="relative w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl"
            >
              <div className="border-b border-gray-100 px-6 pb-4 pt-5">
                <button
                  type="button"
                  aria-label="Close"
                  className="absolute right-3 top-3 rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                  onClick={() => {
                    setShowConfirmationModal(false);
                    setConfirmationNotes('');
                  }}
                  disabled={confirmationLoading}
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
                <h3 className="pr-8 text-lg font-semibold text-gray-900">
                  {job.confirmation_reason === 'schedule_change'
                    ? 'Can you still make this appointment?'
                    : 'Confirm this appointment'}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {job.confirmation_reason === 'schedule_change'
                    ? 'The time changed. Let us know if you can still attend.'
                    : 'Let us know you will be there.'}
                </p>
                <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 rounded-xl bg-gray-50 px-3 py-2.5 text-sm text-gray-700">
                  <span className="inline-flex items-center">
                    <CalendarIcon className="mr-1.5 h-4 w-4 text-gray-400" />
                    {formatDate(job.scheduled_date)}
                  </span>
                  <span className="inline-flex items-center">
                    <ClockIcon className="mr-1.5 h-4 w-4 text-gray-400" />
                    {formatTime(job.scheduled_time)}
                  </span>
                  {job.service_type_name && (
                    <span className="inline-flex items-center font-medium text-gray-900">
                      {job.service_type_name}
                    </span>
                  )}
                </div>
              </div>

              <div className="max-h-[60vh] space-y-5 overflow-y-auto px-6 py-5">
                {promptForTeamMemberOnConfirm && (
                  <div>
                    <p className="text-sm font-medium text-gray-900">Who will attend?</p>
                    <div className="mt-2 max-h-52 space-y-2 overflow-y-auto pr-1">
                      {teamMembers.map((member) => {
                        const selected = String(confirmTeamMemberId) === String(member.id);
                        const initials = `${member.first_name?.[0] || ''}${member.last_name?.[0] || ''}`.toUpperCase();
                        return (
                          <button
                            key={member.id}
                            type="button"
                            onClick={() => handleConfirmTeamMemberChange(String(member.id))}
                            disabled={confirmationLoading}
                            className={`flex w-full items-center rounded-xl border px-3 py-2.5 text-left transition-colors ${
                              selected
                                ? 'border-blue-600 bg-blue-50 ring-1 ring-blue-600'
                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            <span className={`mr-3 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                              selected ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {initials || '?'}
                            </span>
                            <span className="text-sm font-medium text-gray-900">
                              {member.first_name} {member.last_name}
                            </span>
                            {selected && <CheckCircleIcon className="ml-auto h-5 w-5 shrink-0 text-blue-600" />}
                          </button>
                        );
                      })}
                      <button
                        type="button"
                        onClick={() => handleConfirmTeamMemberChange(NEW_TEAM_MEMBER_OPTION)}
                        disabled={confirmationLoading}
                        className={`flex w-full items-center rounded-xl border border-dashed px-3 py-2.5 text-left text-sm ${
                          isAddingNewTeamMember
                            ? 'border-blue-600 bg-blue-50 text-blue-700 ring-1 ring-blue-600'
                            : 'border-gray-300 text-gray-600 hover:border-gray-400 hover:bg-gray-50'
                        }`}
                      >
                        <span className="mr-3 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-base text-gray-500 ring-1 ring-gray-200">
                          +
                        </span>
                        Add a new team member
                      </button>
                    </div>

                    {isAddingNewTeamMember && (
                      <div className="mt-3 grid grid-cols-2 gap-3">
                        <div>
                          <label htmlFor="confirm_new_first_name" className="mb-1 block text-xs font-medium text-gray-600">
                            First name
                          </label>
                          <input
                            id="confirm_new_first_name"
                            type="text"
                            value={newMemberFirstName}
                            onChange={(e) => setNewMemberFirstName(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={confirmationLoading}
                          />
                        </div>
                        <div>
                          <label htmlFor="confirm_new_last_name" className="mb-1 block text-xs font-medium text-gray-600">
                            Last name
                          </label>
                          <input
                            id="confirm_new_last_name"
                            type="text"
                            value={newMemberLastName}
                            onChange={(e) => setNewMemberLastName(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={confirmationLoading}
                          />
                        </div>
                      </div>
                    )}

                    {requiresCertNumber && confirmSavedCertNumber && !editingConfirmCert && (
                      <div className="mt-3 flex items-center justify-between rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5">
                        <div>
                          <p className="text-xs font-medium text-emerald-700">Certification on file</p>
                          <p className="text-sm font-semibold text-emerald-900">{confirmSavedCertNumber}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setEditingConfirmCert(true)}
                          className="text-xs font-medium text-emerald-800 underline hover:text-emerald-950"
                          disabled={confirmationLoading}
                        >
                          Change
                        </button>
                      </div>
                    )}

                    {requiresCertNumber && (!confirmSavedCertNumber || editingConfirmCert) && (
                      <div className="mt-3">
                        <label htmlFor="confirm_cert_number" className="mb-1 block text-xs font-medium text-gray-600">
                          Certification number
                        </label>
                        <input
                          id="confirm_cert_number"
                          type="text"
                          value={confirmCertNumber}
                          onChange={(e) => setConfirmCertNumber(e.target.value)}
                          placeholder="Enter the certification number"
                          maxLength={100}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          disabled={confirmationLoading}
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Needed for {job.service_type_name || 'this'} appointments.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <label htmlFor="confirmation_notes" className="mb-1 block text-xs font-medium text-gray-600">
                    Notes <span className="font-normal text-gray-400">(optional)</span>
                  </label>
                  <textarea
                    id="confirmation_notes"
                    value={confirmationNotes}
                    onChange={(e) => setConfirmationNotes(e.target.value)}
                    placeholder="Anything we should know"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={2}
                    maxLength={500}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between gap-3 border-t border-gray-100 bg-gray-50 px-6 py-4">
                <Button
                  variant="outline-danger"
                  onClick={() => handleConfirmation('declined')}
                  disabled={confirmationLoading}
                >
                  {confirmationLoading ? 'Processing...' : "Can't attend"}
                </Button>
                <Button
                  variant="success"
                  onClick={() => handleConfirmation('confirmed')}
                  disabled={confirmationLoading || !agencyConfirmReady}
                >
                  <CheckCircleIcon className="mr-2 h-4 w-4" />
                  {confirmationLoading ? 'Processing...' : 'Confirm attendance'}
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      )}

      {/* Mileage Prompt Modal */}
      {showMileagePrompt && (
        <div className="fixed inset-0 z-50 overflow-y-auto overscroll-contain bg-black bg-opacity-50 touch-pan-y [-webkit-overflow-scrolling:touch]">
          <div className="flex min-h-[100vh] min-h-dvh w-full items-center justify-center px-4 py-6 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1.25rem,env(safe-area-inset-bottom))] pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))]">
            <div className="relative w-full max-w-md shrink-0 rounded-lg bg-white p-5 shadow-lg sm:p-8">
            {/* Close Button */}
            <button
              onClick={() => {
        setShowMileagePrompt(false);
        setShowMileageFields(false);
        setMileageMilesInput('0');
        setMileageRate(0.70);
      }}
      className="absolute right-2 top-2 flex h-11 w-11 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 sm:right-4 sm:top-4"
      aria-label="Close"
    >
      <XCircleIcon className="h-7 w-7 sm:h-6 sm:w-6" />
    </button>
    
    <div className="text-center mb-6">
      <CheckCircleIcon className="h-16 w-16 text-blue-600 mx-auto mb-4" />
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        Mileage Reimbursement
      </h2>
      <p className="text-gray-600 text-sm">
        Do you need to be reimbursed for mileage to this job location?
      </p>
    </div>

    <div className="space-y-4">
      <div className="bg-red-50 border border-red-200 rounded-md p-3">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700">
              <strong>Please note:</strong> Mileage requests for assignments under a 25-mile radius will not be approved. Please avoid entering mileage unless it is truly over 25 miles from your location.
            </p>
          </div>
        </div>
      </div>
      {!showMileageFields ? (
        <div className="space-y-3">
          <p className="text-sm text-gray-800 text-center rounded-md border border-blue-200 bg-blue-50 px-3 py-2.5 leading-snug">
            Tap <span className="font-semibold text-blue-900">Enter miles & rate</span> below to open the form. Miles and per-mile rate are filled in on the next step after you tap.
          </p>
          <div className="flex flex-col gap-2 sm:flex-row sm:space-x-3 sm:gap-0">
            <button
              type="button"
              onClick={() => setShowMileageFields(true)}
              disabled={mileagePromptLoading}
              className="min-h-[48px] flex-1 touch-manipulation rounded-md border-2 border-blue-600 bg-white px-4 py-3 text-base font-semibold text-blue-900 shadow-sm hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 sm:py-2.5 sm:text-sm"
            >
              Enter miles & rate
            </button>
            <button
              type="button"
              onClick={handleNoMileage}
              disabled={mileagePromptLoading}
              className="min-h-[48px] flex-1 touch-manipulation rounded-md bg-blue-600 px-4 py-3 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 sm:py-2.5 sm:text-sm"
            >
              {mileagePromptLoading ? 'Submitting...' : 'Proceed with no mileage'}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Miles to job location:
            </label>
            <input
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={mileageMilesInput}
              onChange={(e) => {
                const v = e.target.value;
                if (isPartialMilesInput(v)) setMileageMilesInput(v);
              }}
              className="w-full rounded-md border border-gray-300 px-3 py-3 text-base focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 sm:py-2"
              placeholder="0"
            />
            <p className="text-xs text-gray-500 mt-1">
              Clear the field and enter your miles, or use Back if you do not need mileage.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rate per mile ($):
            </label>
            <input
              type="number"
              min="0"
              max={FEDERAL_MILEAGE_CAP}
              step="0.01"
              value={mileageRate}
              onChange={(e) => setMileageRate(Math.min(FEDERAL_MILEAGE_CAP, Math.max(0, parseFloat(e.target.value) || 0)))}
              className="w-full rounded-md border border-gray-300 px-3 py-3 text-base focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 sm:py-2"
              placeholder="0.70"
            />
            <p className="text-xs text-gray-500 mt-1">
              Federal cap: ${FEDERAL_MILEAGE_CAP}/mile. Your rate will be capped if higher.
            </p>
            {mileageRequested > 0 && (
              <p className="text-sm text-gray-500 mt-1">
                Estimated reimbursement: ${(mileageRequested * Math.min(FEDERAL_MILEAGE_CAP, mileageRate || 0.70)).toFixed(2)}
              </p>
            )}
          </div>
        </>
      )}

              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      <strong>Important:</strong> If you request mileage reimbursement, your assignment will need admin approval before being confirmed.
                    </p>
                  </div>
                </div>
              </div>

              {showMileageFields && (
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowMileageFields(false);
                      setMileageMilesInput('0');
                    }}
                    disabled={mileagePromptLoading}
                    className="min-h-[48px] flex-1 touch-manipulation rounded-md bg-gray-200 px-4 py-3 text-base font-medium text-gray-800 hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50 sm:py-2 sm:text-sm"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleMileageSubmit}
                    disabled={mileagePromptLoading || mileageRequested <= 0}
                    className="min-h-[48px] flex-1 touch-manipulation rounded-md bg-blue-600 px-4 py-3 text-base font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 sm:py-2 sm:text-sm"
                  >
                    {mileagePromptLoading ? 'Submitting...' : 'Request Mileage'}
                  </button>
                </div>
              )}
            </div>
            </div>
          </div>
        </div>
      )}

      {showTeamMemberModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black bg-opacity-50"
              onClick={() => !assigningTeamMember && setShowTeamMemberModal(false)}
            />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Select Team Member</h3>
                <button
                  type="button"
                  onClick={() => !assigningTeamMember && setShowTeamMemberModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                  disabled={assigningTeamMember}
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Which team member will perform this job?
              </p>
              {teamMembers.length === 0 ? (
                <p className="text-sm text-gray-500 mb-4">
                  No team members yet. Add them from Team Members first.
                </p>
              ) : (
                <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg mb-4 divide-y divide-gray-100">
                  {teamMembers.map((member) => (
                    <label
                      key={member.id}
                      className={`flex items-center px-3 py-3 cursor-pointer hover:bg-gray-50 ${
                        String(selectedTeamMemberId) === String(member.id) ? 'bg-blue-50' : ''
                      }`}
                    >
                      <input
                        type="radio"
                        name="team_member"
                        value={member.id}
                        checked={String(selectedTeamMemberId) === String(member.id)}
                        onChange={() => handleSelectTeamMember(String(member.id))}
                        className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                        disabled={assigningTeamMember}
                      />
                      <span className="ml-3">
                        <span className="block text-sm font-medium text-gray-900">
                          {member.first_name} {member.last_name}
                        </span>
                        {member.languages && member.languages !== 'N/A' && (
                          <span className="block text-xs text-gray-500">{member.languages}</span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              )}
              {requiresCertNumber && teamMemberSavedCertNumber && !editingTeamMemberCert && (
                <p className="mb-4 text-sm text-gray-700">
                  Cert # on file: <span className="font-medium">{teamMemberSavedCertNumber}</span>{' '}
                  <button
                    type="button"
                    onClick={() => setEditingTeamMemberCert(true)}
                    className="text-blue-600 hover:text-blue-700 underline text-xs"
                    disabled={assigningTeamMember}
                  >
                    Use a different number
                  </button>
                </p>
              )}
              {requiresCertNumber && teamMembers.length > 0 && selectedTeamMemberId &&
                (!teamMemberSavedCertNumber || editingTeamMemberCert) && (
                <div className="mb-4">
                  <label htmlFor="team_member_cert_number" className="block text-sm font-medium text-gray-700 mb-1">
                    Certification # *
                  </label>
                  <input
                    id="team_member_cert_number"
                    type="text"
                    value={teamMemberCertNumber}
                    onChange={(e) => setTeamMemberCertNumber(e.target.value)}
                    placeholder="Team member's certification number"
                    maxLength={100}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={assigningTeamMember}
                  />
                </div>
              )}
              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowTeamMemberModal(false)}
                  disabled={assigningTeamMember}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleAssignTeamMember}
                  disabled={
                    assigningTeamMember ||
                    !selectedTeamMemberId ||
                    teamMembers.length === 0 ||
                    (requiresCertNumber && !teamMemberCertNumber.trim())
                  }
                >
                  {assigningTeamMember ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobDetails;


